import { IEventAggregationService } from '@studyportals/event-aggregation-service-interface';
import { IAppRegistration, IRouting, IsAppRegistration, SearchType } from '@studyportals/search-page-interface';
import { Ref, App } from 'vue';
import {
	createRouter,
	createWebHistory,
	Router,
	LocationQueryRaw,
	RouteLocationNormalized,
	NavigationGuardNext,
	RouteLocationNormalizedLoaded,
	RouteRecordRaw,
	RouteLocationRaw,
	RouteLocation,
	RouterOptions,
	LocationQuery
} from 'vue-router';
import { EventAggregationService } from '@/search-page/platform/EventAggregationService';
import { SearchPageNavigationPerformedEvent } from './events/SearchPageNavigationPerformedEvent';
import { match, MatchResult } from 'path-to-regexp';
import ProcessRouteResult from './ProcessRouteResult';
import Logger from '../platform/Logger';
import { PortalType } from '@studyportals/domain-client';

export class Routing implements IRouting {
	private readonly eventAggregationService: IEventAggregationService;
	private readonly applications: IAppRegistration[] = [];
	private numberOfApplications = 3;
	public readonly router: Router;
	private wasRouteMatchFound = false;

	public constructor(eventAggregationService?: IEventAggregationService) {
		if (window.PortalType === PortalType.DISTANCE_LEARNING || window.PortalType === PortalType.SHORT) {
			this.numberOfApplications = 2;
		}

		this.eventAggregationService = eventAggregationService ?? EventAggregationService.instance;

		const options: RouterOptions = {
			history: createWebHistory('/search'),
			routes: [],
			stringifyQuery: Routing.stringifyQuery.bind(undefined),
			parseQuery: Routing.parseQuery.bind(undefined)
		};

		this.router = createRouter(options);
		this.router.beforeEach(this.beforeEach.bind(this));
		this.router.afterEach(this.afterEach.bind(this));
	}

	public get currentRoute(): Ref<RouteLocationNormalizedLoaded> {
		return this.router.currentRoute;
	}

	public get currentApplication(): IAppRegistration | null {
		return this.findApplicationFor(this.currentRoute.value);
	}

	public async navigateTo(location: RouteLocationRaw): Promise<void> {
		try {
			await this.router.push(location);
		} catch {
			//ignored, this shouldn't happen, if it does it's actually not an issue
			Logger.logWarning(`Routing: Error navigating to ${JSON.stringify(location)}`);
		}
	}

	public resolve(location: RouteLocationRaw): Promise<RouteLocation & { href: string }> {
		return Promise.resolve(this.router.resolve(location));
	}

	public registerApplication(application: IAppRegistration): void {
		const isNotAppRegistration = !IsAppRegistration(application);
		if (isNotAppRegistration) {
			return;
		}

		this.applications.push(application);

		if (application.type === SearchType.STUDY) {
			this.router.addRoute(application.routing.routes[0]);

			return;
		}

		if (this.wasRouteMatchFound) return;

		const currentPath = this.router.currentRoute.value.path;

		let processRouteResult: ProcessRouteResult | null = null;
		for (const route of application.routing.routes) {
			processRouteResult = this.processRoute(route, currentPath);
		}

		if (!processRouteResult || !processRouteResult.matched) {
			if (this.applications.length !== this.numberOfApplications) return;

			this.registerAllRoutes();

			return;
		}

		this.wasRouteMatchFound = true;

		void this.navigateTo({
			...this.router.currentRoute.value,
			name: processRouteResult.route.name,
			params: processRouteResult.params
		} as RouteLocationRaw).then(() => {
			if (this.applications.length !== this.numberOfApplications) return;

			this.registerAllRoutes();
		});
	}

	private processRoute(route: RouteRecordRaw, currentPath: string): ProcessRouteResult | null {
		const nestedRoutes = this.collectNestedRoutesWithFullPaths(route);
		let params: MatchResult<object> | null = null;
		let routeFound: RouteRecordRaw | null = null;
		const matchingRouteInfo = nestedRoutes.find(({ fullPath, route: nestedRoute }) => {
			try {
				const matcher = match(fullPath.replace(/\?/g, ''), { decode: decodeURIComponent }) as (
					path: string
				) => MatchResult<object>;
				const matcherResult = matcher(currentPath);

				if (!matcherResult) return false;

				routeFound = nestedRoute;
				params = matcherResult.params as MatchResult<object>;
				return true;
			} catch {
				return false;
			}
		});

		if (matchingRouteInfo) {
			this.registerMinimalRouteTree(route, matchingRouteInfo.route);

			return {
				route: routeFound || route,
				params: params || {},
				matched: true
			};
		}

		return {
			route: routeFound || route,
			params: params || {},
			matched: false
		};
	}

	private collectNestedRoutesWithFullPaths(rootRoute: RouteRecordRaw): { route: RouteRecordRaw; fullPath: string }[] {
		const result: { route: RouteRecordRaw; fullPath: string }[] = [];

		const processRouteWithPath = (route: RouteRecordRaw, parentPath: string): void => {
			const normalizedParentPath = parentPath.endsWith('/') ? parentPath : `${parentPath}/`;

			const routePath = route.path;
			let fullPath: string;

			if (routePath.startsWith('/')) {
				fullPath = routePath;
			} else if (normalizedParentPath === '/' && routePath) {
				fullPath = `/${routePath}`;
			} else {
				fullPath = routePath ? `${normalizedParentPath}${routePath}` : normalizedParentPath.slice(0, -1);
			}

			result.push({ route, fullPath });

			if (route.children) {
				for (const child of route.children) {
					processRouteWithPath(child, fullPath);
				}
			}
		};

		processRouteWithPath(rootRoute, '/');

		return result;
	}

	private registerMinimalRouteTree(rootRoute: RouteRecordRaw, targetRoute: RouteRecordRaw): void {
		if (rootRoute === targetRoute) {
			this.router.addRoute({
				...rootRoute,
				children: []
			});
			return;
		}

		const path = this.findPathToRoute(rootRoute, targetRoute);
		if (!path.length) {
			console.warn('No path found to the target route:', targetRoute);
			return;
		}

		let minimalRoute: RouteRecordRaw | null = null;
		let currentMinimalRoute: RouteRecordRaw | null = null;

		for (const routeInPath of path) {
			const newRoute: RouteRecordRaw = {
				...routeInPath,
				children: []
			};

			if (!minimalRoute) {
				minimalRoute = newRoute;
				currentMinimalRoute = newRoute;
			} else if (currentMinimalRoute) {
				currentMinimalRoute.children = [newRoute];
				currentMinimalRoute = newRoute;
			}
		}

		if (minimalRoute) {
			this.router.addRoute(minimalRoute);
		}
	}

	private findPathToRoute(rootRoute: RouteRecordRaw, targetRoute: RouteRecordRaw): RouteRecordRaw[] {
		if (rootRoute === targetRoute) {
			return [rootRoute];
		}

		if (!rootRoute.children) {
			return [];
		}

		for (const child of rootRoute.children) {
			const path = this.findPathToRoute(child, targetRoute);
			if (path.length > 0) {
				return [rootRoute, ...path];
			}
		}

		return [];
	}
	private async beforeEach(
		to: RouteLocationNormalized,
		from: RouteLocationNormalized,
		next: NavigationGuardNext
	): Promise<void> {
		const application = this.findApplicationFor(to);
		if (application) {
			await application.routing.beforeEach(to, from, next);
		} else {
			next();
		}
	}

	private registerAllRoutes(): void {
		this.router.clearRoutes();

		for (const app of this.applications) {
			app.routing.routes.forEach((route) => {
				this.router.addRoute(route);
			});
		}
	}

	private async afterEach(to: RouteLocationNormalized, from: RouteLocationNormalized): Promise<void> {
		const application = this.findApplicationFor(to);

		this.eventAggregationService.publishTo(
			SearchPageNavigationPerformedEvent.EventType,
			new SearchPageNavigationPerformedEvent(to, application)
		);

		if (application) {
			await application.routing.afterEach(to, from);
		}
	}

	private findApplicationFor(location: RouteLocationNormalized): IAppRegistration | null {
		const matchedRoutePaths = new Set(location.matched.map((r) => r.path));

		if (matchedRoutePaths.size === 0) {
			const matchedApplication = this.applications.find((a) => a.isPathForThisApp?.(location.path));

			return matchedApplication ?? null;
		}

		for (const application of this.applications) {
			const matchedRoute = application.routing.routes
				.flatMap((r) => this.collectRoutesFrom(r))
				.find((route) => matchedRoutePaths.has(route.path));

			if (matchedRoute) return application;
		}
		return null;
	}

	private collectRoutesFrom(route: RouteRecordRaw): RouteRecordRaw[] {
		const result: RouteRecordRaw[] = [];

		const stack: RouteRecordRaw[] = [route];
		while (stack.length > 0) {
			const current = stack.pop()!;
			result.push(current);

			if (current.children) {
				stack.push(...current.children);
			}
		}

		return result;
	}

	public install(app: App): void {
		app.use(this.router);
	}

	private static stringifyQuery(query: LocationQueryRaw): string {
		const filterKeys = Object.keys(query);
		if (filterKeys.length > 0) {
			const parameters: string[] = [];

			for (const filterKey of filterKeys) {
				const valueRaw = query[filterKey];
				if (valueRaw === undefined || valueRaw === null) {
					continue;
				}

				const value = Array.isArray(valueRaw) ? valueRaw.join(',') : valueRaw;
				parameters.push(`${filterKey}=${value}`);
			}

			return parameters.join('&');
		}

		return '';
	}

	private static parseQuery(search: string): LocationQuery {
		const searchNormalized = decodeURIComponent(search.trim());
		if (searchNormalized.length === 0) {
			return {};
		}

		const filters = searchNormalized.split('&');
		const result: LocationQuery = {};

		for (const filter of filters) {
			const [key, valueEncoded] = filter.split('=');

			let values: string[] = [];
			if (valueEncoded) {
				const intervals = valueEncoded.match(/\[.*?\]/g);
				values = intervals !== null ? intervals : valueEncoded.split(',');
			}

			if (values.length > 1) {
				result[key] = values;
			} else {
				result[key] = values[0];
			}
		}

		return result;
	}
}
